import { DateTime } from '@ksyos/date-time';
import {
    Alert,
    Button,
    Card,
    Form,
    FormButtonRow,
    FormGroupContext,
    FormSection,
    Modal,
    PageHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    Text,
} from '@ksyos/design-system';
import { connect } from 'react-redux';
import type { EpisodeCategory, Profession } from '@common/enums';
import type { IActiveInsurance } from '@common/validation/insuranceHistory';
import type { IPatientAddEditValidated } from '@common/validation/patient';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import type { IInsuranceHistory, IOrganizationGetOne } from '@frontend/components/interfaces';
import type { ValidationErrors } from '@frontend/utils';
import { Gender, MaritalStatus, ValidationErrorCode } from '@common/enums';
import { elfproef } from '@common/utils/elfproef';
import { getDisplayName } from '@common/utils/getDisplayName';
import { getObjectLength } from '@common/utils/getObjectLength';
import { activeInsuranceSchema, insuranceHistoryGetSourceSchema } from '@common/validation/insuranceHistory';
import { patientAddEditSchema, patientGetDuplicatesSchema } from '@common/validation/patient';
import { HorizontalFormGroup } from '@frontend/components/elements/forms';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { getIsFlexible } from '@frontend/services/api/episodeCategoriesApiService';
import {
    emptyValidationErrors,
    scrollToFormControlFeedback,
    scrollToValidationError,
    updateValidationErrorsForOne,
    validate,
} from '@frontend/utils';
import type { IReduxState } from '../../../configureStore';
import { AbortableFetch, fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { FormButton } from '../../elements/buttons/formButton/FormButton';
import { CareProviderSelection } from '../../elements/CareProviderSelection';
import {
    DateInput,
    EnumDropdown,
    Feedback,
    InputCheckbox,
    InputText,
    Loader,
    Loading,
    NullableInputText,
    ReadOnlyInputText,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { CountrySelect } from '../../elements/CountrySelect';
import { FormLabel } from '../../elements/forms/FormLabel';
import { FormRow } from '../../elements/forms/FormRow';
import { InputContainer } from '../../elements/forms/InputContainer';
import { StackedInputs } from '../../elements/forms/StackedInputs';
import { StateSelect } from '../../elements/StateSelect';
import { FeedbackForDuplicates } from './FeedbackForDuplicates';

type PatientAddEditProps = IWithRouterProps;

interface IPatientAddEditReduxProps {
    isManager: boolean;
    organizationId: string;
    organizationType: string;
    practitionerId: string;
    practitionerProfession: Profession;
}

export interface IPatient {
    birthDate: string | null;
    careProviderOrganizationId: string | null;
    careProviderPractitionerId: string | null;
    city: string | null;
    country: string | null;
    deceasedDate: string | null;
    emailAddress: string | null;
    gender: Gender | null;
    homePhoneNumber: string | null;
    houseLetter: string | null;
    houseNumber: string | null;
    houseNumberSuffix: string | null;
    identifierInsurance: string | null;
    identifierSocialSecurity: string | null;
    isDeceased: boolean;
    maritalStatus: MaritalStatus | null;
    mobileNumber: string | null;
    nameFamily: string;
    nameFamily2: string | null;
    nameGiven: string;
    nameInfix: string | null;
    nameInfix2: string | null;
    nameUsual: string | null;
    profession: string | null;
    state: string | null;
    street: string | null;
    zip: string | null;
}

interface IPatientLabels extends Omit<IPatient, 'gender' | 'maritalStatus' | 'isDeceased'> {
    gender: string;
    isDeceased: string;
    maritalStatus: string;
}

interface IChangeControl {
    field: keyof IPatient;
    newReadableValue?: string;
    newValue: boolean | string | null;
    oldReadableValue?: string;
    oldValue: boolean | string | null;
}

interface IPatientAddEditState {
    activeInsurance?: IInsuranceHistory;
    authorization: {
        canAdd?: boolean;
        canSelectCareProviderOrganizationAndPractitioner?: boolean;
        canUpdate?: boolean;
    };
    careProviderOrganizations: IOrganization[];
    careProviderPractitioners: IPractitioner[];
    changeControl: IChangeControl[];
    duplicate: {
        id?: string;
        matchField?: string;
    };
    errors: ValidationErrors;
    insuranceLookupServiceIsDown?: boolean;
    insuranceMessage?: string;
    isSaving: boolean;
    isSearching: boolean;
    noMatchDateBsnMessage?: string;
    oldPatientData?: IPatient;
    organization?: IOrganizationGetOne;
    patient?: IPatient;
    showConfirmationModal: boolean;
}

interface IPractitioner {
    id: string;
    nameFamily: string;
    nameGiven: string;
    nameInfix: string;
}

interface IOrganization {
    id: string;
    name: string;
}

const mapStateToProps = ({ authentication }: IReduxState): IPatientAddEditReduxProps => ({
    isManager: authentication.isManager,
    organizationId: authentication.organizationId,
    organizationType: authentication.organizationType,
    practitionerId: authentication.id,
    practitionerProfession: authentication.profession,
});

class InnerPatientsAddEdit extends Component<PatientAddEditProps & IPatientAddEditReduxProps, IPatientAddEditState> {
    private readonly abortableFetch = new AbortableFetch();
    private readonly abortController = new AbortController();

    private duplicateCheckLastId: number;
    private duplicateCheckTimeout: number;
    private updatePersonalDataUrl = '/api/patients/updatePersonalData';

    constructor(props: PatientAddEditProps & IPatientAddEditReduxProps) {
        super(props);

        this.state = {
            authorization: {},
            careProviderOrganizations: [],
            careProviderPractitioners: [],
            changeControl: [],
            duplicate: {},
            errors: emptyValidationErrors,
            isSaving: false,
            isSearching: false,
            showConfirmationModal: false,
        };
    }

    public async componentDidMount() {
        await this.loadPage();
    }

    public async componentWillUnmount() {
        this.abortableFetch.abort();
        this.abortController.abort();
    }

    public async loadPage() {
        if (this.props.router.params.id) {
            await this.loadPatient();
        } else {
            const permissions: { json?: { authorization: { canAdd: boolean } } } = await this.abortableFetch.fetchJson(
                '/api/patients/permissions?includeListAuthorizations=canAdd',
                'GET'
            );

            await this.setState({
                authorization: {
                    canSelectCareProviderOrganizationAndPractitioner: true,
                    canUpdate: false,
                    canAdd: permissions.json ? permissions.json.authorization.canAdd : false,
                },
                patient: {
                    nameGiven: '',
                    nameFamily: this.props.router.searchParams.get('nameFamily') ?? '',
                    nameInfix: null,
                    nameUsual: null,
                    nameFamily2: null,
                    nameInfix2: null,
                    birthDate: this.props.router.searchParams.get('birthDate'),
                    identifierSocialSecurity: null,
                    identifierInsurance: null,
                    street: null,
                    houseNumber: null,
                    houseLetter: null,
                    houseNumberSuffix: null,
                    zip: null,
                    city: null,
                    country: 'NL',
                    state: null,
                    mobileNumber: null,
                    homePhoneNumber: null,
                    emailAddress: null,
                    gender: this.props.router.searchParams.get('gender') as Gender,
                    maritalStatus: null,
                    careProviderOrganizationId: null,
                    careProviderPractitionerId: null,
                    isDeceased: false,
                    deceasedDate: null,
                    profession: null,
                },
            });

            if (this.props.router.params.ssoId) {
                await this.loadSsoData();
            }
        }

        await this.loadOrganization();
    }

    public render() {
        const editModeAndAllowed = !!this.props.router.params.id && this.state.authorization.canUpdate;
        const addModeAndAllowed = !this.props.router.params.id && this.state.authorization.canAdd;

        const patientFieldLabels: IPatientLabels = {
            nameGiven: translateKey('common.nameGiven'),
            nameFamily: translateKey('common.nameFamily'),
            nameInfix: translateKey('common.nameInfix'),
            nameUsual: translateKey('patient.nameUsual'),
            nameFamily2: translateKey('patient.nameFamily2'),
            nameInfix2: translateKey('common.nameInfix'),
            birthDate: translateKey('common.birthDate'),
            identifierSocialSecurity: translateKey('patient.identifierSocialSecurity'),
            identifierInsurance: translateKey('patient.insurancePolicyNumber'),
            street: translateKey('common.street'),
            houseNumber: translateKey('common.numberShort'),
            houseLetter: translateKey('patient.houseLetterShort'),
            houseNumberSuffix: translateKey('patient.houseNumberSuffixShort'),
            zip: translateKey('common.zipcode'),
            city: translateKey('common.city'),
            country: translateKey('common.country'),
            state: translateKey('common.state'),
            mobileNumber: translateKey('common.mobileNumber'),
            homePhoneNumber: translateKey('common.homeNumber'),
            emailAddress: translateKey('common.emailAddress'),
            gender: translateKey('common.gender'),
            maritalStatus: translateKey('patient.maritalStatus'),
            careProviderOrganizationId: translateKey('patient.careProviderOrganization'),
            careProviderPractitionerId: translateKey('patient.careProviderPractitioner'),
            isDeceased: translateKey('patient.isDeceased'),
            deceasedDate: translateKey('patient.deceasedDate'),
            profession: translateKey('common.profession'),
        };

        return (
            <>
                <PageTemplate width="medium">
                    <PageHeader
                        title={
                            this.props.router.params.id
                                ? translateKey('patient.editPatient')
                                : translateKey('patient.newPatient')
                        }
                    />

                    <Loading
                        item={this.state.patient}
                        loaded={(patient) => (
                            <>
                                {this.state.isSearching ? (
                                    <Loader horizontalCentered />
                                ) : (
                                    <FormGroupContext.Provider value={{ size: 'medium', variant: 'flushed' }}>
                                        <Card>
                                            <Form
                                                onSubmit={async (e) => {
                                                    e.preventDefault();
                                                    await this.beforeSaving();
                                                }}
                                                submitOnEnter={false}
                                            >
                                                {(this.props.router.searchParams.get('notFound') === 'true' ||
                                                    this.state.insuranceLookupServiceIsDown) && (
                                                    <Alert appearance="warning">
                                                        {translateKey('patientSso.patientNotFoundServiceDownNoBsn')}
                                                    </Alert>
                                                )}
                                                <Card.Header title={translateKey('patient.details')} />
                                                <Card.Body>
                                                    <FormSection>
                                                        <HorizontalFormGroup
                                                            hasErrors={this.state.errors.has(
                                                                'identifierSocialSecurity'
                                                            )}
                                                        >
                                                            <FormLabel>
                                                                {translateKey('patient.identifierSocialSecurityLong')} (
                                                                {translateKey('patient.identifierSocialSecurity')})
                                                            </FormLabel>
                                                            <InputContainer>
                                                                <NullableInputText
                                                                    disabled={
                                                                        (!!this.props.router.params.id &&
                                                                            !!this.state.oldPatientData
                                                                                ?.identifierSocialSecurity) ||
                                                                        !this.props.isManager
                                                                    }
                                                                    error={this.state.errors.get(
                                                                        'identifierSocialSecurity'
                                                                    )}
                                                                    onChange={async (value) =>
                                                                        await this.handleChange(
                                                                            'identifierSocialSecurity',
                                                                            value
                                                                        )
                                                                    }
                                                                    onDoValidate={async () =>
                                                                        await this.validateOne(
                                                                            'identifierSocialSecurity'
                                                                        )
                                                                    }
                                                                    placeholder={translateKey('common.fillIn')}
                                                                    value={patient.identifierSocialSecurity}
                                                                />
                                                                {this.state.duplicate.matchField ===
                                                                    'identifierSocialSecurity' &&
                                                                    this.state.duplicate.id && (
                                                                        <FeedbackForDuplicates
                                                                            error={translateKey(
                                                                                'patient.duplicateSocialSecurity'
                                                                            )}
                                                                            id={this.state.duplicate.id}
                                                                        />
                                                                    )}
                                                                {this.state.noMatchDateBsnMessage && (
                                                                    <Feedback
                                                                        error={this.state.noMatchDateBsnMessage}
                                                                    />
                                                                )}
                                                            </InputContainer>
                                                            {!this.props.router.params.id && (
                                                                <FormButton
                                                                    disabled={
                                                                        !patient.birthDate ||
                                                                        (patient.identifierSocialSecurity &&
                                                                            !elfproef(
                                                                                patient.identifierSocialSecurity
                                                                            )) ||
                                                                        (!patient.identifierInsurance &&
                                                                            !patient.identifierSocialSecurity &&
                                                                            !(patient.zip && patient.houseNumber))
                                                                    }
                                                                    onClick={async () =>
                                                                        await this.searchForPatientData()
                                                                    }
                                                                >
                                                                    {translateKey('patient.getNameAndInsurance')}
                                                                </FormButton>
                                                            )}
                                                        </HorizontalFormGroup>

                                                        <HorizontalFormGroup
                                                            hasErrors={this.state.errors.has('identifierInsurance')}
                                                        >
                                                            <FormLabel>
                                                                {translateKey('patient.insurancePolicyNumber')}
                                                            </FormLabel>
                                                            <InputContainer>
                                                                <NullableInputText
                                                                    error={this.state.errors.get('identifierInsurance')}
                                                                    onChange={async (value) =>
                                                                        await this.handleChange(
                                                                            'identifierInsurance',
                                                                            value
                                                                        )
                                                                    }
                                                                    onDoValidate={async () =>
                                                                        await this.validateOne('identifierInsurance')
                                                                    }
                                                                    placeholder={translateKey('common.fillIn')}
                                                                    value={patient.identifierInsurance}
                                                                />
                                                                {!this.state.activeInsurance &&
                                                                    patient.identifierInsurance && (
                                                                        <Feedback
                                                                            error={
                                                                                ValidationErrorCode.NO_VALID_INSURANCE
                                                                            }
                                                                        />
                                                                    )}
                                                                {this.state.insuranceMessage && (
                                                                    <Feedback error={this.state.insuranceMessage} />
                                                                )}
                                                            </InputContainer>
                                                        </HorizontalFormGroup>

                                                        <HorizontalFormGroup
                                                            hasErrors={this.state.errors.has('identifierInsurance')}
                                                        >
                                                            <FormLabel>
                                                                {translateKey('common.healthInsurer')}
                                                            </FormLabel>
                                                            <InputContainer>
                                                                <ReadOnlyInputText
                                                                    placeholder={translateKey('common.automatically')}
                                                                    value={
                                                                        this.state.activeInsurance
                                                                            ? this.state.activeInsurance.uzovi.name
                                                                            : null
                                                                    }
                                                                />
                                                            </InputContainer>
                                                        </HorizontalFormGroup>

                                                        <HorizontalFormGroup
                                                            hasErrors={
                                                                this.state.errors.has('nameGiven') ||
                                                                this.state.errors.has('nameInfix') ||
                                                                this.state.errors.has('nameFamily') ||
                                                                this.state.errors.has('nameUsual') ||
                                                                this.state.errors.has('nameFamily2') ||
                                                                this.state.errors.has('nameInfix2')
                                                            }
                                                        >
                                                            <FormLabel isRequired>
                                                                {translateKey('common.name')}
                                                            </FormLabel>
                                                            <StackedInputs>
                                                                <FormRow>
                                                                    <InputContainer>
                                                                        <InputText
                                                                            error={this.state.errors.get('nameGiven')}
                                                                            onChange={async (value) =>
                                                                                await this.handleChange(
                                                                                    'nameGiven',
                                                                                    value
                                                                                )
                                                                            }
                                                                            onDoValidate={async () =>
                                                                                await this.validateOne('nameGiven')
                                                                            }
                                                                            placeholder={translateKey(
                                                                                'common.nameGiven'
                                                                            )}
                                                                            value={patient.nameGiven}
                                                                        />
                                                                    </InputContainer>
                                                                    <InputContainer size="medium">
                                                                        <NullableInputText
                                                                            error={this.state.errors.get('nameInfix')}
                                                                            onChange={async (value) =>
                                                                                await this.handleChange(
                                                                                    'nameInfix',
                                                                                    value
                                                                                )
                                                                            }
                                                                            onDoValidate={async () =>
                                                                                await this.validateOne('nameInfix')
                                                                            }
                                                                            placeholder={translateKey(
                                                                                'common.nameInfix'
                                                                            )}
                                                                            value={patient.nameInfix}
                                                                        />
                                                                    </InputContainer>
                                                                    <InputContainer>
                                                                        <InputText
                                                                            error={this.state.errors.get('nameFamily')}
                                                                            onChange={async (value) =>
                                                                                await this.handleChange(
                                                                                    'nameFamily',
                                                                                    value
                                                                                )
                                                                            }
                                                                            onDoValidate={async () =>
                                                                                await this.validateOne('nameFamily')
                                                                            }
                                                                            placeholder={translateKey(
                                                                                'common.nameFamily'
                                                                            )}
                                                                            value={patient.nameFamily}
                                                                        />
                                                                    </InputContainer>
                                                                </FormRow>
                                                                <FormRow>
                                                                    <InputContainer>
                                                                        <NullableInputText
                                                                            error={this.state.errors.get('nameUsual')}
                                                                            onChange={async (value) =>
                                                                                await this.handleChange(
                                                                                    'nameUsual',
                                                                                    value
                                                                                )
                                                                            }
                                                                            onDoValidate={async () =>
                                                                                await this.validateOne('nameUsual')
                                                                            }
                                                                            placeholder={translateKey(
                                                                                'patient.nameUsual'
                                                                            )}
                                                                            value={patient.nameUsual}
                                                                        />
                                                                    </InputContainer>
                                                                    <InputContainer size="medium">
                                                                        <NullableInputText
                                                                            error={this.state.errors.get('nameInfix2')}
                                                                            onChange={async (value) =>
                                                                                await this.handleChange(
                                                                                    'nameInfix2',
                                                                                    value
                                                                                )
                                                                            }
                                                                            onDoValidate={async () =>
                                                                                await this.validateOne('nameInfix2')
                                                                            }
                                                                            placeholder={translateKey(
                                                                                'common.nameInfix'
                                                                            )}
                                                                            value={patient.nameInfix2}
                                                                        />
                                                                    </InputContainer>
                                                                    <InputContainer>
                                                                        <NullableInputText
                                                                            error={this.state.errors.get('nameFamily2')}
                                                                            onChange={async (value) =>
                                                                                await this.handleChange(
                                                                                    'nameFamily2',
                                                                                    value
                                                                                )
                                                                            }
                                                                            onDoValidate={async () =>
                                                                                await this.validateOne('nameFamily2')
                                                                            }
                                                                            placeholder={translateKey(
                                                                                'patient.nameFamily2'
                                                                            )}
                                                                            value={patient.nameFamily2}
                                                                        />
                                                                    </InputContainer>
                                                                </FormRow>
                                                            </StackedInputs>
                                                        </HorizontalFormGroup>

                                                        <HorizontalFormGroup
                                                            hasErrors={this.state.errors.has('birthDate')}
                                                        >
                                                            <FormLabel isRequired>
                                                                {translateKey('common.birthDate')}
                                                            </FormLabel>
                                                            <InputContainer size="medium">
                                                                <DateInput
                                                                    birthDate
                                                                    error={this.state.errors.get('birthDate')}
                                                                    onChange={async (value) =>
                                                                        await this.handleChange('birthDate', value)
                                                                    }
                                                                    onDoValidate={async (date) =>
                                                                        await this.birthDateValidation(date)
                                                                    }
                                                                    placeholder={translateKey(
                                                                        'patient.birthDatePlaceholder'
                                                                    )}
                                                                    value={patient.birthDate}
                                                                />
                                                                {this.state.duplicate.matchField === 'name' &&
                                                                    this.state.duplicate.id && (
                                                                        <FeedbackForDuplicates
                                                                            error={translateKey(
                                                                                'patient.duplicateName'
                                                                            )}
                                                                            id={this.state.duplicate.id}
                                                                        />
                                                                    )}
                                                            </InputContainer>
                                                        </HorizontalFormGroup>

                                                        <HorizontalFormGroup
                                                            hasErrors={this.state.errors.has('gender')}
                                                        >
                                                            <FormLabel isRequired>
                                                                {translateKey('common.gender')}
                                                            </FormLabel>
                                                            <InputContainer size="medium">
                                                                <EnumDropdown
                                                                    enumName="gender"
                                                                    error={this.state.errors.get('gender')}
                                                                    jsEnum={Gender}
                                                                    onChange={async (value) =>
                                                                        await this.handleChange('gender', value)
                                                                    }
                                                                    onDoValidate={async () =>
                                                                        await this.validateOne('gender')
                                                                    }
                                                                    value={patient.gender}
                                                                />
                                                            </InputContainer>
                                                        </HorizontalFormGroup>

                                                        <HorizontalFormGroup
                                                            hasErrors={
                                                                this.state.errors.has('street') ||
                                                                this.state.errors.has('houseNumber') ||
                                                                this.state.errors.has('houseLetter') ||
                                                                this.state.errors.has('houseNumberSuffix') ||
                                                                this.state.errors.has('zip') ||
                                                                this.state.errors.has('city') ||
                                                                this.state.errors.has('country') ||
                                                                this.state.errors.has('state')
                                                            }
                                                        >
                                                            <FormLabel>{translateKey('patient.address')}</FormLabel>
                                                            <StackedInputs>
                                                                <FormRow>
                                                                    <InputContainer>
                                                                        <NullableInputText
                                                                            error={this.state.errors.get('street')}
                                                                            onChange={async (value) =>
                                                                                await this.handleChange('street', value)
                                                                            }
                                                                            onDoValidate={async () =>
                                                                                await this.validateOne('street')
                                                                            }
                                                                            placeholder={translateKey('common.street')}
                                                                            value={patient.street}
                                                                        />
                                                                    </InputContainer>
                                                                    <InputContainer size="small">
                                                                        <NullableInputText
                                                                            error={this.state.errors.get('houseNumber')}
                                                                            onChange={async (value) =>
                                                                                await this.handleChange(
                                                                                    'houseNumber',
                                                                                    value
                                                                                )
                                                                            }
                                                                            onDoValidate={async () =>
                                                                                await this.validateOne('houseNumber')
                                                                            }
                                                                            placeholder={translateKey(
                                                                                'common.numberShort'
                                                                            )}
                                                                            value={patient.houseNumber}
                                                                        />
                                                                    </InputContainer>
                                                                    <InputContainer size="small">
                                                                        <NullableInputText
                                                                            error={this.state.errors.get('houseLetter')}
                                                                            onChange={async (value) =>
                                                                                await this.handleChange(
                                                                                    'houseLetter',
                                                                                    value
                                                                                )
                                                                            }
                                                                            onDoValidate={async () =>
                                                                                await this.validateOne('houseLetter')
                                                                            }
                                                                            placeholder={translateKey(
                                                                                'patient.houseLetterShort'
                                                                            )}
                                                                            value={patient.houseLetter}
                                                                        />
                                                                    </InputContainer>
                                                                    <InputContainer size="small">
                                                                        <NullableInputText
                                                                            error={this.state.errors.get(
                                                                                'houseNumberSuffix'
                                                                            )}
                                                                            onChange={async (value) =>
                                                                                await this.handleChange(
                                                                                    'houseNumberSuffix',
                                                                                    value
                                                                                )
                                                                            }
                                                                            onDoValidate={async () =>
                                                                                await this.validateOne(
                                                                                    'houseNumberSuffix'
                                                                                )
                                                                            }
                                                                            placeholder={translateKey(
                                                                                'patient.houseNumberSuffixShort'
                                                                            )}
                                                                            value={patient.houseNumberSuffix}
                                                                        />
                                                                    </InputContainer>
                                                                </FormRow>
                                                                <FormRow>
                                                                    <InputContainer size="medium">
                                                                        <NullableInputText
                                                                            error={this.state.errors.get('zip')}
                                                                            onChange={async (value: string | null) =>
                                                                                await this.handleChange(
                                                                                    'zip',
                                                                                    value ? value.toUpperCase() : null
                                                                                )
                                                                            }
                                                                            onDoValidate={async () =>
                                                                                await this.validateOne('zip')
                                                                            }
                                                                            placeholder={translateKey('common.zipcode')}
                                                                            value={patient.zip}
                                                                        />
                                                                    </InputContainer>
                                                                    <InputContainer>
                                                                        <NullableInputText
                                                                            error={this.state.errors.get('city')}
                                                                            onChange={async (value) =>
                                                                                await this.handleChange('city', value)
                                                                            }
                                                                            onDoValidate={async () =>
                                                                                await this.validateOne('city')
                                                                            }
                                                                            placeholder={translateKey('common.city')}
                                                                            value={patient.city}
                                                                        />
                                                                    </InputContainer>
                                                                </FormRow>
                                                                <FormRow>
                                                                    <InputContainer>
                                                                        <CountrySelect
                                                                            error={this.state.errors.get('country')}
                                                                            onChange={async (country) =>
                                                                                await this.handleChange(
                                                                                    'country',
                                                                                    country
                                                                                )
                                                                            }
                                                                            onDoValidate={async () =>
                                                                                await this.validateOne('country')
                                                                            }
                                                                            value={patient.country}
                                                                        />
                                                                    </InputContainer>
                                                                </FormRow>
                                                                <FormRow>
                                                                    <InputContainer>
                                                                        <StateSelect
                                                                            country={patient.country}
                                                                            error={this.state.errors.get('state')}
                                                                            onChange={async (state) =>
                                                                                await this.handleChange('state', state)
                                                                            }
                                                                            onDoValidate={async () =>
                                                                                await this.validateOne('state')
                                                                            }
                                                                            value={patient.state}
                                                                        />
                                                                    </InputContainer>
                                                                </FormRow>
                                                            </StackedInputs>
                                                        </HorizontalFormGroup>

                                                        <HorizontalFormGroup
                                                            hasErrors={this.state.errors.has('maritalStatus')}
                                                        >
                                                            <FormLabel>
                                                                {translateKey('patient.maritalStatus')}
                                                            </FormLabel>
                                                            <InputContainer>
                                                                <EnumDropdown
                                                                    enumName="maritalStatus"
                                                                    error={this.state.errors.get('maritalStatus')}
                                                                    jsEnum={MaritalStatus}
                                                                    onChange={async (value) =>
                                                                        await this.handleChange('maritalStatus', value)
                                                                    }
                                                                    onDoValidate={async () =>
                                                                        await this.validateOne('maritalStatus')
                                                                    }
                                                                    value={patient.maritalStatus}
                                                                />
                                                            </InputContainer>
                                                        </HorizontalFormGroup>
                                                        <HorizontalFormGroup
                                                            hasErrors={this.state.errors.has('profession')}
                                                        >
                                                            <FormLabel>{translateKey('common.profession')}</FormLabel>
                                                            <InputContainer>
                                                                <NullableInputText
                                                                    error={this.state.errors.get('profession')}
                                                                    onChange={async (value) =>
                                                                        await this.handleChange('profession', value)
                                                                    }
                                                                    onDoValidate={async () =>
                                                                        await this.validateOne('profession')
                                                                    }
                                                                    placeholder={translateKey('common.profession')}
                                                                    value={patient.profession}
                                                                />
                                                            </InputContainer>
                                                        </HorizontalFormGroup>

                                                        <HorizontalFormGroup
                                                            hasErrors={
                                                                this.state.errors.has('mobileNumber') ||
                                                                this.state.errors.has('homePhoneNumber')
                                                            }
                                                        >
                                                            <FormLabel>{translateKey('common.telephone')}</FormLabel>
                                                            <InputContainer>
                                                                <NullableInputText
                                                                    error={this.state.errors.get('mobileNumber')}
                                                                    onChange={async (value) =>
                                                                        await this.handleChange('mobileNumber', value)
                                                                    }
                                                                    onDoValidate={async () =>
                                                                        await this.validateOne('mobileNumber')
                                                                    }
                                                                    placeholder={translateKey('common.mobileNumber')}
                                                                    value={patient.mobileNumber}
                                                                />
                                                            </InputContainer>
                                                            <InputContainer>
                                                                <NullableInputText
                                                                    error={this.state.errors.get('homePhoneNumber')}
                                                                    onChange={async (value) =>
                                                                        await this.handleChange(
                                                                            'homePhoneNumber',
                                                                            value
                                                                        )
                                                                    }
                                                                    onDoValidate={async () =>
                                                                        await this.validateOne('homePhoneNumber')
                                                                    }
                                                                    placeholder={translateKey('common.homeNumber')}
                                                                    value={patient.homePhoneNumber}
                                                                />
                                                            </InputContainer>
                                                        </HorizontalFormGroup>

                                                        <HorizontalFormGroup
                                                            hasErrors={this.state.errors.has('emailAddress')}
                                                        >
                                                            <FormLabel>{translateKey('common.emailAddress')}</FormLabel>
                                                            <InputContainer>
                                                                <NullableInputText
                                                                    error={this.state.errors.get('emailAddress')}
                                                                    onChange={async (value) =>
                                                                        await this.handleChange('emailAddress', value)
                                                                    }
                                                                    onDoValidate={async () =>
                                                                        await this.validateOne('emailAddress')
                                                                    }
                                                                    placeholder={translateKey('common.fillIn')}
                                                                    value={patient.emailAddress}
                                                                />
                                                            </InputContainer>
                                                        </HorizontalFormGroup>
                                                    </FormSection>

                                                    <FormSection title={translateKey('patient.registerGP')}>
                                                        {this.state.authorization
                                                            .canSelectCareProviderOrganizationAndPractitioner && (
                                                            <CareProviderSelection
                                                                automation="choosePractitioner"
                                                                error={
                                                                    this.state.errors.get(
                                                                        'careProviderOrganizationId'
                                                                    ) ||
                                                                    this.state.errors.get('careProviderPractitionerId')
                                                                }
                                                                formlabeltext={translateKey('common.selectGp')}
                                                                hasErrors={
                                                                    this.state.errors.has(
                                                                        'careProviderOrganizationId'
                                                                    ) ||
                                                                    this.state.errors.has('careProviderPractitionerId')
                                                                }
                                                                initialOrganizationId={
                                                                    patient.careProviderOrganizationId
                                                                }
                                                                initialPractitionerId={
                                                                    patient.careProviderPractitionerId
                                                                }
                                                                onChange={async (organizationId, practitionerId) => {
                                                                    await this.handleChange(
                                                                        'careProviderOrganizationId',
                                                                        organizationId
                                                                    );
                                                                    await this.handleChange(
                                                                        'careProviderPractitionerId',
                                                                        practitionerId
                                                                    );
                                                                }}
                                                                onDoValidate={async () => {
                                                                    await this.validateOne(
                                                                        'careProviderOrganizationId'
                                                                    );
                                                                    await this.validateOne(
                                                                        'careProviderPractitionerId'
                                                                    );
                                                                }}
                                                                size="medium"
                                                            />
                                                        )}

                                                        {this.props.router.params.id && (
                                                            <HorizontalFormGroup>
                                                                <FormLabel>
                                                                    {translateKey('patient.isDeceased')}
                                                                </FormLabel>
                                                                <InputContainer>
                                                                    <InputCheckbox
                                                                        checked={patient.isDeceased}
                                                                        error={this.state.errors.get('isDeceased')}
                                                                        onChange={async (value) =>
                                                                            await this.handleChange('isDeceased', value)
                                                                        }
                                                                    />
                                                                </InputContainer>
                                                            </HorizontalFormGroup>
                                                        )}

                                                        {patient.isDeceased && (
                                                            <HorizontalFormGroup
                                                                hasErrors={this.state.errors.has('deceasedDate')}
                                                            >
                                                                <FormLabel>
                                                                    {translateKey('patient.deceasedDate')}
                                                                </FormLabel>
                                                                <InputContainer>
                                                                    <DateInput
                                                                        onChange={async (value) =>
                                                                            await this.handleChange(
                                                                                'deceasedDate',
                                                                                value
                                                                            )
                                                                        }
                                                                        placeholder={translateKey('common.date')}
                                                                        value={patient.deceasedDate}
                                                                    />
                                                                </InputContainer>
                                                            </HorizontalFormGroup>
                                                        )}
                                                    </FormSection>

                                                    <FormButtonRow>
                                                        <Button
                                                            isDisabled={this.state.isSaving}
                                                            onClick={() => this.goBack()}
                                                            variant="outline"
                                                        >
                                                            {translateKey('common.cancel')}
                                                        </Button>
                                                        <Button
                                                            isDisabled={
                                                                this.state.isSaving ||
                                                                (!addModeAndAllowed && !editModeAndAllowed)
                                                            }
                                                            type="submit"
                                                            variant="solid"
                                                        >
                                                            {translateKey('patient.savePatientData')}
                                                        </Button>
                                                    </FormButtonRow>
                                                </Card.Body>
                                            </Form>
                                        </Card>
                                    </FormGroupContext.Provider>
                                )}
                            </>
                        )}
                    />
                </PageTemplate>

                <Modal
                    isOpen={this.state.showConfirmationModal}
                    onClose={async () => await this.setState({ showConfirmationModal: false })}
                    size="large"
                >
                    <Modal.Header>Bevestig aanpassingen</Modal.Header>
                    <Modal.CloseButton />
                    <Modal.Body>
                        <Text mb={4}>Je hebt aanpassingen gedaan aan de patiëntgegevens:</Text>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader></TableHeader>
                                        <TableHeader>{translateKey('common.old')}</TableHeader>
                                        <TableHeader>{translateKey('common.new')}</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {this.state.changeControl.map((change) => (
                                        <TableRow key={change.field}>
                                            <TableCell>{patientFieldLabels[change.field]}</TableCell>
                                            <TableCell>{change.oldReadableValue}</TableCell>
                                            <TableCell>{change.newReadableValue}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Text mt={4}>Wil je deze aanpassing doorvoeren?</Text>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            onClick={async () => await this.setState({ showConfirmationModal: false })}
                            variant="outline"
                        >
                            Annuleren
                        </Button>
                        <Button onClick={async () => await this.save()}>Doorvoeren</Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }

    private goBack() {
        if (this.props.router.params.id) {
            this.props.router.navigate(-1);
        } else {
            this.props.router.navigate('/home');
        }
    }

    private async loadPractitionerName(practitionerId: string) {
        const result = await fetchJson(`/api/practitioners/${practitionerId}`, 'GET');

        return getDisplayName(result.json);
    }

    private async loadOrganizationName(organizationId: string) {
        const result = await fetchJson(`/api/organizations/${organizationId}`, 'GET');

        return result.json.name;
    }

    private async getFieldReadableValue<K extends keyof IPatient>(field: K, value: IPatient[K]) {
        let readableValue: string;
        if (value === undefined || value === null) {
            readableValue = '';
        } else if (field === 'careProviderPractitionerId') {
            readableValue = await this.loadPractitionerName(value as string);
        } else if (field === 'careProviderOrganizationId') {
            readableValue = await this.loadOrganizationName(value as string);
        } else if (field === 'maritalStatus' || field === 'gender') {
            readableValue = translateKey(`${field}Enum.${value}`);
        } else if (typeof value === 'boolean') {
            readableValue = value ? translateKey('common.yes') : translateKey('common.no');
        } else {
            readableValue = value + '';
        }

        return readableValue;
    }

    private async handleChangeControl<K extends keyof IPatient>(field: K, value: IPatient[K]) {
        const newChange = {
            field,
            oldValue: this.state.oldPatientData ? this.state.oldPatientData[field] : null,
            newValue: value,
        };

        await this.setState((prevState) => {
            const fieldIndex = prevState.changeControl.findIndex((e: IChangeControl) => e.field === field);
            if (fieldIndex === -1) {
                return { changeControl: [...prevState.changeControl, newChange] };
            } else {
                const changeControl: IChangeControl[] = [...prevState.changeControl];
                changeControl[fieldIndex] = newChange;

                return {
                    changeControl,
                };
            }
        });
    }

    private async handleChange<K extends keyof IPatient>(name: K, value: IPatient[K]) {
        if (this.props.router.params.id) {
            await this.handleChangeControl(name, value);
        }

        if (name === 'identifierSocialSecurity') {
            await this.setState(({ patient }) => ({
                patient: { ...patient, identifierSocialSecurity: ((value as string) || '').trim() },
            }));
        } else {
            await this.setState(({ patient }) => ({ patient: { ...patient, [name]: value } }));
        }

        await this.checkDuplicates();
        if (name === 'birthDate' || name === 'identifierSocialSecurity') {
            await this.setState({ noMatchDateBsnMessage: undefined });
        }
    }

    private async validateOne(name: keyof IPatient) {
        this.trimPatientData(this.state.patient);
        await this.setState(({ patient, errors }) => ({
            errors: updateValidationErrorsForOne(patientAddEditSchema, patient, errors, name),
        }));
    }

    private async loadPatient() {
        const patient = await this.getPatient();
        const activeInsurance = await this.getActiveInsurance();

        if (patient) {
            await this.setState({
                authorization: {
                    canSelectCareProviderOrganizationAndPractitioner:
                        patient.authorization.canChangeCareProviderOrganization &&
                        patient.authorization.canChangeCareProviderPractitioner,
                    canUpdate: patient.authorization.canUpdate,
                },
                patient: {
                    ...patient,
                    identifierInsurance: activeInsurance ? activeInsurance.insurancePolicyNumber : null,
                },
                oldPatientData: { ...patient },
                activeInsurance,
            });
        } else {
            await this.setState(({ authorization }) => ({
                authorization: { ...authorization, canSelectCareProviderOrganizationAndPractitioner: true },
            }));
        }
    }

    private async getPatient() {
        const url = `/api/patients/${this.props.router.params.id}?includeItemAuthorizations=canUpdate,canChangeCareProviderPractitioner,canChangeCareProviderOrganization`;
        const result = await this.abortableFetch.fetchJson(url, 'GET');

        return result.json;
    }

    private async getActiveInsurance() {
        const url = `/api/patients/${this.props.router.params.id}/insuranceHistory?isActive=true`;
        const result = await this.abortableFetch.fetchJson(url, 'GET');

        return result.json.items.length > 0 ? result.json.items[0] : null;
    }

    private async loadSsoData() {
        const url = `/api/thirdPartySsoData/${this.props.router.params.ssoId}`;
        const result = await this.abortableFetch.fetchJson(url, 'GET');
        const ssoData = result.json.data.patient;

        const identifierInsurance = ssoData.insurancePolicyNumber;
        delete ssoData.insurancePolicyNumber;
        delete ssoData.action;
        delete ssoData.actionServiceId;
        await this.setState(({ patient }) => ({ patient: { ...patient, ...ssoData, identifierInsurance } }));
    }

    private trimPatientData(patient: any) {
        Object.keys(patient).forEach((key) => {
            if (patient[key] && typeof patient[key] === 'string') {
                patient[key] = patient[key].trim();
            }
        });
    }

    private checkDuplicates() {
        if (this.duplicateCheckTimeout) {
            clearTimeout(this.duplicateCheckTimeout);
        }
        const timerId = (this.duplicateCheckTimeout = window.setTimeout(async () => {
            if (this.state.patient) {
                this.duplicateCheckLastId = timerId;

                const { errors: newErrors } = validate(patientGetDuplicatesSchema, this.state.patient);

                if (newErrors.size > 0) {
                    if (this.state.duplicate) {
                        this.setState({ duplicate: {} });
                    }

                    return;
                }

                const result = await this.abortableFetch.fetchJson(
                    '/api/patients/duplicates',
                    'POST',
                    this.state.patient
                );

                if (timerId === this.duplicateCheckLastId) {
                    // check if this request is the last one started. If another was sent later we are only interested in that one.
                    if (result.json.count > 0) {
                        await this.setState(({ errors }) => ({
                            duplicate: result.json.items[0],
                            errors: errors.remove('identifierSocialSecurity'),
                        }));
                    } else {
                        await this.setState({ duplicate: {} });
                    }
                }
            }
        }, 1000));
    }

    private async searchForPatientData() {
        await this.setState({ isSearching: true, insuranceLookupServiceIsDown: false });
        await this.updatePersonalDataAndInsurance();
        await this.setState({ isSearching: false });
    }

    private async updatePersonalDataAndInsurance() {
        if (this.state.patient && this.state.patient.identifierSocialSecurity) {
            const result = await this.abortableFetch.fetchJson(this.updatePersonalDataUrl, 'POST', {
                bsn: this.state.patient.identifierSocialSecurity,
                birthDate: this.state.patient.birthDate,
            });

            if (result.json.patient) {
                await this.setState({ noMatchDateBsnMessage: undefined });
                const patient = result.json.patient;
                await this.setState({ patient: { ...this.state.patient, ...patient } });
                await this.updateActiveInsurance(false);
            } else {
                if (!(await this.updateActiveInsurance(true)) && !this.state.insuranceLookupServiceIsDown) {
                    const message = this.props.router.params.id
                        ? 'noMatchDateBsnTextUpdate'
                        : 'noMatchDateBsnTextCreate';
                    await this.setState({ noMatchDateBsnMessage: message });
                }
            }
        } else {
            await this.updateActiveInsurance(true);
        }
    }

    private async birthDateValidation(value: string | null) {
        if (!value || !DateTime.from(value).isValid()) {
            await this.setState(({ errors }) => ({ errors: errors.set('birthDate', 'any_required') }));

            return false;
        } else if (DateTime.from(value).isAfter(DateTime.now(), 'day')) {
            await this.setState(({ errors }) => ({ errors: errors.set('birthDate', 'date_future') }));

            return false;
        } else {
            await this.setState(({ errors }) => ({ errors: errors.remove('birthDate') }));
        }

        return true;
    }

    private async updateActiveInsurance(updatePersonalDataFields: boolean) {
        if (this.state.patient) {
            if (!(await this.birthDateValidation(this.state.patient.birthDate))) {
                return;
            }

            const patient = {
                ...this.state.patient,
                birthDate:
                    this.state.patient.birthDate && DateTime.from(this.state.patient.birthDate).format('YYYY-MM-DD'),
            };

            const { errors, item } = validate(insuranceHistoryGetSourceSchema, patient);
            await this.setState({ errors });
            if (errors.size > 0) {
                return false;
            }

            const result = await this.abortableFetch.fetchJson('/api/insuranceHistories/fromSource?', 'POST', item);

            if (result.json.sourceResult && result.json.sourceResult.insurance) {
                const activeInsurance = {
                    insurancePolicyNumber: result.json.sourceResult.insurance.identifierInsurance,
                    startDate: DateTime.from(result.json.sourceResult.insurance.startdate).format('YYYY-MM-DD'),
                    endDate: result.json.sourceResult.insurance.enddate
                        ? DateTime.from(result.json.sourceResult.insurance.enddate).format('YYYY-MM-DD')
                        : undefined,
                    uzovi: {
                        name: result.json.sourceResult.insurance.name,
                        code: result.json.sourceResult.insurance.uzoviCode,
                    },
                };
                await this.setState((prevState) => ({
                    ...prevState,
                    activeInsurance,
                    patient: {
                        ...prevState.patient,
                        identifierInsurance: activeInsurance.insurancePolicyNumber,
                    },
                }));
                if (updatePersonalDataFields) {
                    await this.setState((prevState) => ({
                        patient: {
                            ...prevState.patient,
                            nameGiven: result.json.sourceResult.nameGiven || null,
                            nameInfix: result.json.sourceResult.nameInfix || null,
                            nameFamily: result.json.sourceResult.nameFamily || null,
                            nameFamily2: result.json.sourceResult.nameFamily2 || null,
                            nameInfix2: result.json.sourceResult.nameInfix2 || null,
                            gender: result.json.sourceResult.gender || null,
                            isDeceased: result.json.sourceResult.isDeceased || false,
                            birthDate: result.json.sourceResult.birthDate || null,
                            identifierSocialSecurity: result.json.sourceResult.identifierSocialSecurity || null,
                        },
                    }));
                }

                return true;
            } else {
                if (result.json.sourceResult && result.json.sourceResult.error === 'ServiceDown') {
                    await this.setState({ insuranceLookupServiceIsDown: true });
                }

                return false;
            }
        }
    }

    private async beforeSaving() {
        if (this.props.router.params.id && this.state.changeControl.length > 0) {
            const { errors } = validate(patientAddEditSchema, this.state.patient);
            await this.setState({ errors });
            if (errors.size > 0) {
                scrollToValidationError();

                return;
            }

            if (Object.keys(this.state.duplicate).length > 0) {
                scrollToFormControlFeedback();

                return;
            }

            const changes: IChangeControl[] = [];
            for (const change of this.state.changeControl) {
                changes.push({
                    ...change,
                    oldReadableValue: await this.getFieldReadableValue(change.field, change.oldValue),
                    newReadableValue: await this.getFieldReadableValue(change.field, change.newValue),
                });
            }
            await this.setState({ showConfirmationModal: true, changeControl: changes });
        } else {
            await this.save();
        }
    }

    private async save() {
        const forwardToEpisode = this.props.router.searchParams.get('forwardToEpisode');
        const serviceId = this.props.router.searchParams.get('serviceId');
        const withProcedure = this.props.router.searchParams.get('withProcedure');
        const newProcessCustomIdentifier = this.props.router.searchParams.get('newProcessCustomIdentifier');

        if (this.state.duplicate.id) {
            return;
        }
        let url = '/api/patients';
        if (this.props.router.params.id) {
            url += `/${this.props.router.params.id}`;
        }

        if (this.props.router.params.ssoId) {
            url += `?ssoId=${this.props.router.params.ssoId}`;
        }

        const { errors, item } = validate(patientAddEditSchema, this.state.patient);
        await this.setState({ errors });
        if (errors.size > 0) {
            scrollToValidationError();

            return;
        }
        const data: { activeInsurance?: IActiveInsurance | {}; patient: IPatientAddEditValidated | {} } = {
            patient: { ...item },
        };
        if (
            this.state.activeInsurance &&
            getObjectLength(this.state.activeInsurance) > 0 &&
            this.state.patient &&
            this.state.patient.identifierInsurance
        ) {
            const activeInsurance = {
                ...this.state.activeInsurance,
                insurancePolicyNumber: this.state.patient.identifierInsurance,
            };
            const validationResult = validate(activeInsuranceSchema, activeInsurance);
            data.activeInsurance = { ...validationResult.item };
        }
        if (this.props.router.params.id) {
            await this.setState({ isSaving: true });
            await this.abortableFetch.fetchJson(url, 'POST', data);
            this.props.router.navigate(-1);
        } else {
            await this.setState({ isSaving: true });
            const result = await this.abortableFetch.fetchJson(url, 'POST', data);

            if (forwardToEpisode) {
                const isFlexibleCarePath = await getIsFlexible(
                    forwardToEpisode as EpisodeCategory,
                    result.json.id,
                    this.abortController.signal
                );

                if (isFlexibleCarePath) {
                    this.props.router.navigate(`/patient/${result.json.id}/${forwardToEpisode}`);
                } else {
                    const params = new URLSearchParams({
                        episodeCategory: forwardToEpisode,
                        patientId: result.json.id,
                        ...(serviceId ? { serviceId } : {}),
                        ...(withProcedure === 'true' ? { withProcedure } : {}),
                        ...(newProcessCustomIdentifier ? { newProcessCustomIdentifier } : {}),
                    });
                    this.props.router.navigate(`/episode?${params.toString()}`);
                }
            } else {
                this.props.router.navigate(`/patients/${result.json.id}`);
            }
        }
    }

    private async loadOrganization() {
        const url = `/api/organizations/${this.props.organizationId}`;
        const result = await this.abortableFetch.fetchJson(url, 'GET');
        await this.setState({ organization: result.json });
    }
}

export const PatientsAddEdit = connect(mapStateToProps)(withRouter(InnerPatientsAddEdit));
